import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import HomePage from "@/pages/HomePage";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { SiteContentProvider } from "@/i18n/SiteContentContext";

const GalleryPage = lazy(() => import("@/pages/GalleryPage"));
const TestimonialsPage = lazy(() => import("@/pages/TestimonialsPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const CareersPage = lazy(() => import("@/pages/CareersPage"));
const AdminLoginPage = lazy(() => import("@/pages/AdminLoginPage"));
const AdminDashboardPage = lazy(() => import("@/pages/AdminDashboardPage"));

const PageLoader = () => (
  <div className="grid min-h-screen place-items-center bg-navy font-jakarta text-sm font-bold uppercase tracking-[0.3em] text-gold">
    Loading…
  </div>
);

const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -80, duration: 1.4 });
        else if (el) el.scrollIntoView();
      }, 120);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.25, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <LanguageProvider>
          <SiteContentProvider>
            <ScrollManager />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/gallery" element={<GalleryPage />} />
                <Route path="/testimonials" element={<TestimonialsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/careers" element={<CareersPage />} />
                <Route path="/admin" element={<AdminLoginPage />} />
                <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
              </Routes>
            </Suspense>
            <Toaster position="bottom-right" richColors />
          </SiteContentProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
